import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IAppState } from "store/app-state";
import { useGetPins, useAddPinGifMutation, useUnPinGifMutation, useReorderPinGifsMutation } from "queries";
import { routes } from "routes";
import { IGif } from "services/types";
import { showNotification } from "utils/notification";

type TUsePinsGif = {
  userId: string | undefined;
}

export type TOnPinParams = {
  gif: IGif;
  available: boolean;
}

export const usePinsGif = ({ userId }: TUsePinsGif) => {
  const { username, verified } = useSelector((state: IAppState) => state.user.currentUser);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [pins, setPins] = useState<IGif[]>([]);

  const addPinGifMutation = useAddPinGifMutation();
  const unPinGifMutation = useUnPinGifMutation();
  const reorderPinGifsMutation = useReorderPinGifsMutation();

  const showPinsIcons = useMemo(() =>
    routes.user(username).includes(pathname) && verified,
  [username, pathname, verified]);

  const {
    data,
    isSuccess
  } = userId ? useGetPins(userId) : { data: undefined, isSuccess: false };

  const onReorderUpdate = async (gifs: IGif[]) => {
    try {
      const gifsIds = gifs.map(gif => gif.id);

      await reorderPinGifsMutation.mutateAsync(gifsIds);
    } catch (err) {
      console.error(err);

      showNotification({
        variant: "error",
        title: "Could not reorder GIF, please try later",
      });
    }
  };

  const onChangePins = (gifs: IGif[]) => {
    setPins(gifs);
    onReorderUpdate(gifs);
  };

  const handlePinGif = async (gif: IGif) => {
    try {
      await addPinGifMutation.mutateAsync(gif.id);
      setPins(prevPins => [...prevPins, gif]);
    } catch (err) {
      console.error(err);

      showNotification({
        variant: "error",
        title: "Could not pin GIF, please try later",
      });
    }
  };

  const handleUnPinGif = async (gif: IGif) => {
    try {
      await unPinGifMutation.mutateAsync(gif.id);
      setPins(prevPins => prevPins.filter(prev => prev.id !== gif.id));
    } catch (err) {
      console.error(err);

      showNotification({
        variant: "error",
        title: "Could not unpin GIF, please try later",
      });
    }
  };

  const onPinGif = ({ gif, available }: TOnPinParams) => {
    const isPinedGif = pins.some(pin => pin.id === gif.id);

    if (!available) {
      return showNotification({
        variant: "info",
        title: "Only public content that has a description can be pinned",
        description: "You can update both from the manage page",
        actionButton: {
          title: "Go to the Manage page",
          onClick: () => navigate(routes.manageProfile())
        }
      });
    }

    if (pins.length >= 4 && !isPinedGif) {
      return showNotification({
        variant: "info",
        title: "You have already 4 pinned GIFs",
      });
    }

    if (isPinedGif) {
      handleUnPinGif(gif);
    } else {
      handlePinGif(gif);
    }
  };

  useEffect(() => {
    if (isSuccess && data) {
      setPins(data.gifs);
    }
  }, [isSuccess, data]);

  return {
    pins,
    showPinsIcons,
    onPinGif,
    onChangePins,
    onReorderUpdate
  };
};
